import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useCart } from '../../context/CartContext';
import Header from '../Header';

const ProductDetail = () => {
  const { id } = useParams();
  const { addToCart } = useCart();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedSize, setSelectedSize] = useState(null);
  const [specsOpen, setSpecsOpen] = useState(true);
  const [sizeGuideOpen, setSizeGuideOpen] = useState(false);

  // Inject responsive styles
  useEffect(() => {
    const styleId = 'product-detail-styles';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = `
        .product-detail-main {
          padding-top: 150px;
          display: flex;
          min-height: 100vh;
        }
        
        .product-detail-images {
          width: 66.666%;
          padding-bottom: 10px;
        }
        
        .product-detail-image {
          height: 100vh;
          margin-left: 10px;
        }
        
        .product-detail-info {
          position: fixed;
          bottom: 40px;
          right: 20px;
          width: calc(33.333% - 40px);
          z-index: 50;
        }
        
        @media (max-width: 768px) {
          .product-detail-main {
            flex-direction: column;
            padding-top: 80px;
            padding-bottom: 280px;
          }
          
          .product-detail-images {
            width: 100%;
            padding-bottom: 0;
          }
          
          .product-detail-image {
            height: auto;
            margin-left: 0;
            margin-bottom: 10px;
          }
          
          .product-detail-image img {
            height: auto !important;
          }
          
          .product-detail-info {
            position: fixed;
            bottom: 0;
            left: 0;
            right: 0;
            width: 100%;
            padding: 24px 16px;
            background-color: #0D0D0D;
            border-top: 1px solid #1C1C1C;
            z-index: 50;
            max-height: 50vh;
            overflow-y: auto;
          }
        }
      `;
      document.head.appendChild(style);
    }
  }, []);

  useEffect(() => {
    fetchProduct();
  }, [id]);

  const fetchProduct = async () => {
    try {
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;
      setProduct(data);
      
      // Set default selected size
      if (data.sizes && data.sizes.length > 0) {
        setSelectedSize(data.sizes[0]);
      }
    } catch (error) {
      console.error('Error fetching product:', error);
    } finally {
      setLoading(false);
    }
  };

  const getPrice = () => {
    if (!product) return 0;
    if (product.prices && selectedSize) {
      return product.prices[selectedSize] || product.price;
    }
    return product.price;
  };

  const handleAddToCart = () => {
    const cartItem = {
      id: product.id,
      name: product.name,
      price: getPrice(),
      size: selectedSize,
      image: product.image_url,
      quantity: 1,
    };

    addToCart(cartItem);
  };

  // Get all product images
  const getProductImages = () => {
    if (!product) return [];
    const images = [product.image_url];
    if (product.image_url_2) images.push(product.image_url_2);
    if (product.image_url_3) images.push(product.image_url_3);
    return images;
  };

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        backgroundColor: '#0D0D0D',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#8A8A8A',
        fontFamily: "'TASA Orbiter Display', sans-serif",
      }}>
        Loading...
      </div>
    );
  }

  if (!product) {
    return (
      <div style={{
        minHeight: '100vh',
        backgroundColor: '#0D0D0D',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#8A8A8A',
        fontFamily: "'TASA Orbiter Display', sans-serif",
      }}>
        Product not found.
      </div>
    );
  }

  const images = getProductImages();
  const specifications = product.specifications || [];

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#0D0D0D',
      fontFamily: "'TASA Orbiter Display', sans-serif",
    }}>
      <Helmet>
        <title>{product.name} | RTK</title>
        <meta name="description" content={`Shop ${product.name} — RTK.`} />
      </Helmet>
      <Header />

      {/* Main Content */}
      <main className="product-detail-main">
        {/* Left Side - Scrollable Images */}
        <div className="product-detail-images">
          {images.map((imageUrl, index) => (
            <div
              key={index}
              className="product-detail-image"
              style={{
                marginBottom: index < images.length - 1 ? '10px' : 0,
              }}
            >
              <img
                src={imageUrl}
                alt={`${product.name} - Image ${index + 1}`}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: index === 0 && product.category === 'prints' ? 'contain' : 'cover',
                  display: 'block',
                }}
              />
            </div>
          ))}
        </div>

        {/* Right Side - Product Info */}
        <div className="product-detail-info">
            {/* Product Name and Price */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '40px',
            }}>
              <h1 style={{
                fontSize: '16px',
                fontWeight: '400',
                color: '#F1F1F1',
                margin: 0,
              }}>
                {product.name}
              </h1>
              <span style={{
                fontSize: '16px',
                fontWeight: '400',
                color: '#F1F1F1',
              }}>
                € {getPrice()}
              </span>
            </div>

            {/* Size Selector */}
            {product.sizes && product.sizes.length > 0 && (
              <div style={{
                marginBottom: '10px',
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '12px 16px',
                  border: '1px solid #333',
                  cursor: 'pointer',
                  position: 'relative',
                }}>
                  <span style={{
                    fontSize: '14px',
                    color: '#F1F1F1',
                  }}>
                    {selectedSize}
                  </span>
                  <select
                    value={selectedSize}
                    onChange={(e) => setSelectedSize(e.target.value)}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      opacity: 0,
                      cursor: 'pointer',
                    }}
                  >
                    {product.sizes.map((size) => (
                      <option key={size} value={size}>
                        {size}
                      </option>
                    ))}
                  </select>
                  <span style={{
                    fontSize: '14px',
                    color: '#6f6f6f',
                  }}>
                    {product.sizes.length} Size Options ▼
                  </span>
                </div>
              </div>
            )}

            {/* Add to Cart Button */}
            <button
              onClick={handleAddToCart}
              disabled={product.sold_out}
              style={{
                width: '100%',
                padding: '14px',
                backgroundColor: product.sold_out ? '#333' : '#F1F1F1',
                color: product.sold_out ? '#8A8A8A' : '#020202',
                border: 'none',
                fontSize: '16px',
                fontWeight: '400',
                fontFamily: "'TASA Orbiter Display', sans-serif",
                cursor: product.sold_out ? 'not-allowed' : 'pointer',
                marginBottom: '40px',
                transition: 'opacity 0.2s ease',
              }}
              onMouseEnter={(e) => {
                if (!product.sold_out) e.target.style.opacity = '0.9';
              }}
              onMouseLeave={(e) => {
                e.target.style.opacity = '1';
              }}
            >
              {product.sold_out ? 'Sold Out' : 'Add to Cart'}
            </button>

            {/* Specifications Accordion */}
            <div>
              <button
                onClick={() => setSpecsOpen(!specsOpen)}
                style={{
                  width: '100%',
                  padding: '0 0 20px 0',
                  backgroundColor: 'transparent',
                  border: 'none',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  cursor: 'pointer',
                }}
              >
                <span style={{
                  fontSize: '14px',
                  color: '#6f6f6f',
                  fontFamily: "'TASA Orbiter Display', sans-serif",
                }}>
                  Specifications
                </span>
                <span style={{
                  fontSize: '14px',
                  color: '#6f6f6f',
                  transform: specsOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s ease',
                }}>
                  ▼
                </span>
              </button>
              
              {specsOpen && specifications.length > 0 && (
                <ul style={{
                  listStyle: 'none',
                  padding: '0 0 16px 0',
                  margin: 0,
                }}>
                  {specifications.map((spec, index) => (
                    <li
                      key={index}
                      style={{
                        fontSize: '14px',
                        color: '#6f6f6f',
                        marginBottom: '8px',
                        paddingLeft: '12px',
                        position: 'relative',
                      }}
                    >
                      <span style={{
                        position: 'absolute',
                        left: 0,
                      }}>
                        •
                      </span>
                      {spec}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Size Guide Accordion */}
            {product.category === 'prints' && <div>
              <button
                onClick={() => setSizeGuideOpen(!sizeGuideOpen)}
                style={{
                  width: '100%',
                  padding: '20px 0 20px 0',
                  backgroundColor: 'transparent',
                  border: 'none',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  cursor: 'pointer',
                }}
              >
                <span style={{
                  fontSize: '14px',
                  color: '#6f6f6f',
                  fontFamily: "'TASA Orbiter Display', sans-serif",
                }}>
                  Size Guide
                </span>
                <span style={{
                  fontSize: '14px',
                  color: '#6f6f6f',
                  transform: sizeGuideOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s ease',
                }}>
                  ▼
                </span>
              </button>
              
              {sizeGuideOpen && (
                <div style={{
                  padding: '0 0 16px 0',
                  fontSize: '14px',
                  color: '#6f6f6f',
                }}>
                  <p style={{ margin: '0 0 8px 0' }}>A5: 15 × 20 cm</p>
                  <p style={{ margin: '0 0 8px 0' }}>A4: 21 × 29.7 cm</p>
                  <p style={{ margin: '0 0 8px 0' }}>A3: 29.7 × 42 cm</p>
                  <p style={{ margin: '0 0 8px 0' }}>A2: 42 × 59.4 cm</p>
                  <p style={{ margin: 0 }}>A1: 59.4 × 84.1 cm</p>
                </div>
              )}
            </div>}
          </div>
      </main>
    </div>
  );
};

export default ProductDetail;